import { createSlice } from "@reduxjs/toolkit";

// Initial state of the dashboard slice
export const initialState = {
  // State for publisher data
  publisher: { data: null, searchVal: "", debouncedVal: null, isLoading: true, error: null },
  // State for consumer data
  consumer: { data: null, searchVal: "", debouncedVal: null, isLoading: true, error: null },
  // State for service publisher data
  servicePublisher: { data: null, searchVal: "", debouncedVal: null, isLoading: true, error: null },
  // State for service consumer data
  serviceConsumer: { data: null, searchVal: "", debouncedVal: null, isLoading: true, error: null },
  // State for meri consumer data
  meriConsumer: { data: null, searchVal: "", debouncedVal: null, isLoading: true, error: null },
  // State for total transactions
  totalTransactions: { data: null, isLoading: true, error: null },
  // State for category data
  category: { data: null, isLoading: true, error: null },
  // State for debounced inputs
  debouncedInputs: {},
  // State for card data
  cards: {
    totalConsumer: null,
    totalPublisher: null,
    totalOrganization: null,
    totalApi: {
      totalCount: null, // Overall API count
      listingApis: null,
      serviceApis: null,
      documentApis: null,
    },
    isLoading: true,
    error: null,
  },
  // State for date range selection
  dateRange: [
    {
      startDate: null,
      endDate: null,
      key: "selection",
      title: "custom",
      titleKey: "custom",
    },
  ],
};

// Create a Redux slice for the dashboard
const dashboardSlice = createSlice({
  name: "dashboard", // Name of the slice
  initialState, // Initial state
  reducers: {
    // Action to set loading state for a specific key
    setLoading: (state, action) => {
      const { key } = action.payload;
      state[key].isLoading = true;
      state[key].error = null;
    },

    // Action to set error state for a specific key
    setError: (state, action) => {
      const { key, payload } = action.payload;
      state[key].isLoading = false;
      state[key].error = payload;
    },

    // Action to set data for a specific key
    setData: (state, action) => {
      const { key, payload } = action.payload;
      state[key].data = payload;
      state[key].isLoading = false;
      state[key].error = null;
    },

    // Action to set search value for a specific key
    setSearchValue: (state, action) => {
      const { key, payload } = action.payload;
      state[key].searchVal = payload;
    },

    // Action to set debounced value for a specific key
    setDebouncedVal: (state, action) => {
      const { key, payload } = action.payload;
      state[key].debouncedVal = payload;
    },

    // Action to set debounce timeout for a specific key
    setDebounceTimeout: (state, action) => {
      const { key, timeoutId } = action.payload;
      state.debouncedInputs[key] = { timeoutId };
    },

    // Action to set card data
    setCardData: (state, action) => {
      const { payload } = action.payload;
      state.cards = {
        ...state.cards,
        ...payload, // Spread the payload to update card-specific fields
        isLoading: false,
        error: null,
      };
    },

    // Action to set date range with preparation for ISO format
    setDateRange: {
      reducer: (state, action) => {
        state.dateRange = action.payload;
      },
      prepare: (payload) => ({
        payload: payload.map((item) => ({
          ...item,
          startDate: item?.startDate ? new Date(item.startDate).toISOString() : null,
          endDate: item?.endDate ? new Date(item.endDate).toISOString() : null,
        })),
      }),
    },

    // Action to reset date range to its initial value
    resetDateRange: (state) => {
      state.dateRange = initialState.dateRange;
    },
  },
});

// Export actions for use in components
export const {
  setLoading,
  setError,
  setData,
  setCardData,
  setDateRange,
  resetDateRange,
  setSearchValue,
  setDebouncedVal,
  setDebounceTimeout,
} = dashboardSlice.actions;

// Export reducer to be used in the store
export default dashboardSlice.reducer;
